import { useState, useMemo } from "react";
import { ChevronDown, ChevronUp, BookOpen } from "lucide-react";
import { getOrderedGrades } from "@/stores/adminStore";
import { useStorefrontSettings } from "@/stores/storefrontSettingsStore";
import {
  useConditionGuideStore,
  normalizeDescLine,
  loadConditionGuide,
  saveConditionGuide,
  type ConditionGuideEntry,
  type DescriptionLine,
} from "@/stores/conditionGuideStore";

// Re-exports preserved for existing imports across the codebase.
export { loadConditionGuide, saveConditionGuide };
export type { ConditionGuideEntry, DescriptionLine };

const gradeStyleMap: Record<string, string> = {
  "Grade A": "grade-badge-a",
  "Grade B+": "grade-badge-bplus",
  "Grade B": "grade-badge-b",
};

const ConditionGuide = () => {
  const [open, setOpen] = useState(false);
  const sfSettings = useStorefrontSettings();
  const entries = useConditionGuideStore(s => s.entries);
  const guide = useMemo(() => {
    const allGrades = getOrderedGrades();
    return entries.filter(entry => entry.grade === "__standalone__" || !entry.grade || allGrades.includes(entry.grade));
  }, [entries]);

  if (!sfSettings.gradeGuideEnabled || guide.length === 0) return null;

  const gradeEntries = guide.filter(e => e.grade && e.grade !== "__standalone__");
  const standaloneEntries = guide.filter(e => !e.grade || e.grade === "__standalone__");

  const renderLine = (d: string | DescriptionLine, i: number) => {
    const line = normalizeDescLine(d);
    const sizeClass = line.size === "small" ? "text-[10px]" : line.size === "large" ? "text-sm" : "text-xs";
    const formatClass = [
      line.bold ? "font-bold" : "",
      line.italic ? "italic" : "",
      line.strikethrough ? "line-through" : "",
      line.monospace ? "font-mono" : "",
    ].filter(Boolean).join(" ");

    return (
      <li key={i} className={`${sizeClass} ${formatClass}`}>
        {line.bullet ? "• " : ""}{line.text}
      </li>
    );
  };

  const guideStyle: React.CSSProperties = {
    ...(sfSettings.gradeGuideBgColor ? { backgroundColor: sfSettings.gradeGuideBgColor } : {}),
    ...(sfSettings.gradeGuideBorderColor ? { borderColor: sfSettings.gradeGuideBorderColor } : {}),
    ...(sfSettings.gradeGuideEffect === "glow" && sfSettings.gradeGuideBorderColor
      ? { boxShadow: `0 0 16px 2px ${sfSettings.gradeGuideBorderColor}40` } : {}),
  };

  const effectClass = sfSettings.gradeGuideEffect === "pulse" ? "animate-pulse" :
    sfSettings.gradeGuideEffect === "gradient" ? "bg-gradient-to-r from-primary/10 via-accent/20 to-primary/10" : "";

  return (
    <div data-condition-guide className={`rounded-xl shadow-card overflow-hidden border-2 ${!sfSettings.gradeGuideBorderColor ? 'border-primary/20' : ''} ${!sfSettings.gradeGuideBgColor ? 'bg-card' : ''} ${effectClass}`} style={guideStyle}>
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-5 py-4 text-left transition-colors hover:bg-primary/5">
        <div className="flex items-center gap-2">
          <BookOpen className={`w-5 h-5 ${!sfSettings.gradeGuideIconColor ? "text-primary" : ""}`} style={sfSettings.gradeGuideIconColor ? { color: sfSettings.gradeGuideIconColor } : undefined} />
          <div>
            <span className="font-display text-sm font-bold" style={sfSettings.gradeGuideTitleColor ? { color: sfSettings.gradeGuideTitleColor } : undefined}>
              {sfSettings.gradeGuideTitle}
            </span>
            <p className="text-[10px] text-muted-foreground mt-0.5">Tap to understand our grading system</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[10px] font-semibold text-primary bg-primary/10 px-2 py-0.5 rounded-full">
            {gradeEntries.length} grade{gradeEntries.length !== 1 ? "s" : ""}
          </span>
          {open ? <ChevronUp className="w-4 h-4 text-primary" /> : <ChevronDown className="w-4 h-4 text-primary" />}
        </div>
      </button>
      {open && (
        <div className="px-5 pb-5 space-y-3 animate-slide-up">
          {standaloneEntries.map((entry, idx) => (
            <div key={`standalone-${idx}`} className="rounded-lg p-4 border-2 border-accent bg-accent/10">
              <ul className="space-y-1 text-card-foreground leading-relaxed">
                {entry.description.map((d, i) => renderLine(d, i))}
              </ul>
              {entry.note && <p className="mt-2 text-xs font-semibold text-card-foreground/80 italic">{entry.note}</p>}
            </div>
          ))}
          {gradeEntries.map((entry) => (
            <div key={entry.grade} className={`rounded-lg p-4 ${gradeStyleMap[entry.grade] || "border-2 border-primary/30 bg-primary/5"}`}>
              <h4 className="font-display font-bold text-base mb-2">{entry.grade}</h4>
              <ul className="space-y-1 text-card-foreground leading-relaxed">
                {entry.description.map((d, i) => renderLine(d, i))}
              </ul>
              {entry.note && <p className="mt-2 text-xs font-semibold text-card-foreground/80 italic">{entry.note}</p>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default ConditionGuide;
